// Copied from https://github.com/juliencrn/usehooks-ts/blob/master/packages/usehooks-ts/src/useEventListener/useEventListener.ts
import { useEffect, useRef } from 'react';
import { useIsomorphicLayoutEffect } from './useIsomorphicLayoutEffect';
/**
 * Хук подключающий обработчик события к DOM элементу, окну или медиа-запросу
 * @template KW - Тип события для событий window.
 * @template KH - Тип события для HTML или SVG элементов.
 * @template KM - Тип события для списка медиа запросов.
 * @template T - The type of the DOM element (default is `HTMLElement`).
 * @param {KW | KH | KM} eventName - Название целевого события.
 * @param {(event: WindowEventMap[KW] | HTMLElementEventMap[KH] | SVGElementEventMap[KH] | MediaQueryListEventMap[KM] | Event) => void} handler - Функция обработки события (нет необходимости мемоизировать)
 * @param {RefObject<T>} [element] - Ref с DOM элементом или медиа-запросом, на который нужно подключить обработчик события. (по-умолчанию windows)
 * @param {boolean | AddEventListenerOptions} [options] - Параметры, передаваемые в обработчик события.
 * @public
 * @see [Documentation](https://usehooks-ts.com/react-hook/use-event-listener)
 * @example
 * ```tsx
 * // Пример 1: Обработка события window
 * useEventListener('resize', (event) => console.log('Window resized');
 * ```
 * @example
 * ```tsx
 * // Пример 2: Обработка события document с передачей параметра в обработчик
 * const elementRef = useRef(document);
 * useEventListener('click', handleClick, elementRef, { capture: true });
 * ```
 * @example
 * ```tsx
 * // Пример 3: Подключение обработчика события к элементу
 * const buttonRef = useRef<HTMLButtonElement>(null);
 * useEventListener('click', handleButtonClick, buttonRef);
 * ```
 */
function useEventListener(eventName, handler, element, options) {
    // Создаем ref для хранения обработчика. Нужен для стабильности ссылки на функцию-обработчик
    const savedHandlerRef = useRef(handler);
    // Обновляем ref при каждом обновлении, чтобы запускать актуальную версию обработчика
    useIsomorphicLayoutEffect(() => {
        savedHandlerRef.current = handler;
    }, [handler]);
    useEffect(() => {
        // Определяем целевой объект для слушателя событий
        const targetElement = element?.current ?? window;
        if (!(targetElement && targetElement.addEventListener))
            return;
        // Создаем слушатель событий, который вызывает сохраненную функцию-обработчик
        const listener = event => {
            savedHandlerRef.current(event);
        };
        targetElement.addEventListener(eventName, listener, options);
        // Удаляем обработчик при очистке
        return () => {
            targetElement.removeEventListener(eventName, listener, options);
        };
    }, [eventName, element, options]);
}
export { useEventListener };
